import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Modal, Input } from 'antd';
import {
  SearchOutlined,
  ClockCircleOutlined,
  AccountBookOutlined,
  EditOutlined,
  HomeOutlined,
  FileTextOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import EmptyState from './EmptyState';

const ROUTES = [
  { path: '/', name: '用户中心', icon: <HomeOutlined />, keywords: '首页 dashboard' },
  { path: '/time', name: '时间统计', icon: <ClockCircleOutlined />, keywords: 'time 报告 统计' },
  { path: '/bill', name: '剑飞记账', icon: <AccountBookOutlined />, keywords: 'bill 账单 记账' },
  { path: '/write', name: '语写', icon: <EditOutlined />, keywords: 'write 语音写作' },
];

const QUICK_ACTIONS = [
  { id: 'new-report', name: '新建时间报告', icon: <FileTextOutlined />, keywords: '报告 时间', action: () => { window.location.href = '/time'; } },
  { id: 'new-bill', name: '记一笔', icon: <AccountBookOutlined />, keywords: '记账 账单', action: () => { window.location.href = '/bill'; } },
  { id: 'start-write', name: '开始语写', icon: <EditOutlined />, keywords: '语写 写作', action: () => { window.location.href = '/write'; } },
];

const RECENT_VISITS_KEY = 'sj_recent_visits';
const CONTENT_INDEX_KEY = 'sj_spotlight_index';

const getRecentVisits = () => {
  try {
    const raw = localStorage.getItem(RECENT_VISITS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
};

const saveRecentVisit = (item) => {
  try {
    const visits = getRecentVisits();
    const filtered = visits.filter(v => v.path !== item.path);
    const next = [{ path: item.path, name: item.name || item.title, keywords: item.keywords }, ...filtered].slice(0, 5);
    localStorage.setItem(RECENT_VISITS_KEY, JSON.stringify(next));
  } catch {}
};

const getContentIndex = () => {
  try {
    const raw = localStorage.getItem(CONTENT_INDEX_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
};

function highlightText(text, query) {
  if (!query.trim() || typeof text !== 'string') return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <span key={i} style={{ color: 'var(--sj-primary)', fontWeight: 700 }}>{part}</span>
    ) : part
  );
}

function SpotlightSearch({ visible, onClose }) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  const recentVisits = useMemo(() => getRecentVisits(), []);
  const contentIndex = useMemo(() => getContentIndex(), []);

  const flatItems = useMemo(() => {
    if (!query.trim()) {
      const sections = [];
      sections.push({ type: 'section', title: '快捷操作' });
      QUICK_ACTIONS.forEach(a => sections.push({ ...a, type: 'action' }));
      if (recentVisits.length > 0) {
        sections.push({ type: 'section', title: '最近访问' });
        recentVisits.forEach(v => sections.push({ ...v, type: 'route', icon: <HistoryOutlined /> }));
      }
      return sections;
    }

    const q = query.toLowerCase();
    const items = [];

    const matchedActions = QUICK_ACTIONS.filter(a => a.name.includes(q) || a.keywords.includes(q));
    if (matchedActions.length > 0) {
      items.push({ type: 'section', title: '快捷操作' });
      matchedActions.forEach(a => items.push({ ...a, type: 'action' }));
    }

    const matchedRoutes = ROUTES.filter(r => r.name.includes(q) || r.keywords.includes(q) || r.path.includes(q));
    if (matchedRoutes.length > 0) {
      items.push({ type: 'section', title: '页面' });
      matchedRoutes.forEach(r => items.push({ ...r, type: 'route' }));
    }

    const matchedContent = contentIndex.filter(c =>
      (c.name && c.name.toLowerCase().includes(q)) ||
      (c.title && c.title.toLowerCase().includes(q)) ||
      (c.keywords && c.keywords.toLowerCase().includes(q))
    );
    if (matchedContent.length > 0) {
      items.push({ type: 'section', title: '内容' });
      matchedContent.forEach(c => items.push({ ...c, type: 'content', icon: <FileTextOutlined /> }));
    }

    return items;
  }, [query, recentVisits, contentIndex]);

  const selectableItems = useMemo(() => flatItems.filter(i => i.type !== 'section'), [flatItems]);

  useEffect(() => {
    if (visible) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [visible]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleSelect = useCallback((item) => {
    if (!item) return;
    if (item.type === 'action' && item.action) {
      item.action();
    } else if (item.path) {
      saveRecentVisit(item);
      window.location.href = item.path;
    }
    onClose();
  }, [onClose]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = Math.min(selectedIndex + 1, selectableItems.length - 1);
      setSelectedIndex(next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = Math.max(selectedIndex - 1, 0);
      setSelectedIndex(prev);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = selectableItems[selectedIndex];
      if (item) {
        handleSelect(item);
      }
    }
  };

  const flatSelectedIndex = useMemo(() => {
    let selectableIdx = -1;
    for (let i = 0; i < flatItems.length; i++) {
      if (flatItems[i].type !== 'section') selectableIdx++;
      if (selectableIdx === selectedIndex) return i;
    }
    return -1;
  }, [flatItems, selectedIndex]);

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={520}
      className="sj-spotlight-modal"
      destroyOnClose
    >
      <Input
        ref={inputRef}
        prefix={<SearchOutlined style={{ color: 'var(--sj-text-tertiary)' }} />}
        placeholder="搜索页面、功能..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        style={{ marginBottom: 12 }}
        size="large"
      />
      {flatItems.length > 0 ? (
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {flatItems.map((item, index) => {
            if (item.type === 'section') {
              return (
                <div key={`section-${item.title}-${index}`} style={{
                  padding: '8px 12px 4px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--sj-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}>
                  {item.title}
                </div>
              );
            }

            const isSelected = index === flatSelectedIndex;
            const displayName = item.name || item.title;

            return (
              <div
                key={`${item.type}-${item.path || item.id}-${index}`}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => {
                  let si = -1;
                  for (let i = 0; i <= index; i++) {
                    if (flatItems[i].type !== 'section') si++;
                  }
                  setSelectedIndex(si);
                }}
                style={{
                  cursor: 'pointer',
                  borderRadius: 8,
                  padding: '10px 12px',
                  margin: '2px 0',
                  transition: 'background 0.15s',
                  background: isSelected ? '#f0f5ff' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <span style={{ color: 'var(--sj-primary)', marginRight: 10, fontSize: 16, display: 'flex', alignItems: 'center' }}>
                  {item.icon || <FileTextOutlined />}
                </span>
                <span style={{ color: 'var(--sj-text-primary)', fontWeight: 500, flex: 1 }}>
                  {highlightText(displayName, query)}
                </span>
                {item.path && (
                  <span style={{ marginLeft: 'auto', color: 'var(--sj-text-tertiary)', fontSize: 12 }}>
                    {item.path}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <EmptyState preset="search" />
      )}
      <div style={{ marginTop: 8, textAlign: 'center', color: 'var(--sj-text-tertiary)', fontSize: 12 }}>
        {query.trim() ? '↑↓ 选择 · Enter 打开 · Esc 关闭' : 'Enter 打开 · Esc 关闭'}
      </div>
    </Modal>
  );
}

export default SpotlightSearch;
